import {
  Box,
  Typography,
  Grid,
  ThemeProvider,
  Stack,
  Paper,
  Fade,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import HeaderBar, { CurrentPages } from '../header-bar/HeaderBar';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import TaskIcon from '@mui/icons-material/Task';
import { useNavigate } from 'react-router-dom';
import theme from '../resources/NodeTheme';
import { useDiaryItems, DiaryItem } from '../hooks/useDiaryItems';
import { useDocuments, DocumentItem } from '../hooks/useDocuments';
import { useTasks } from '../hooks/useTasks';
import { useInstructions } from '../hooks/useInstructions';
import { useDescriptions } from '../hooks/useDescriptions';
import { format } from 'date-fns';
import GradientBackground from '../resources/components/GradientBackground';
import React from 'react';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';
import DocumentGridItem from '../components/DocumentGridItem';
import FullSizePreview from '../components/FullSizePreview';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InfoIcon from '@mui/icons-material/Info';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import DownloadIcon from '@mui/icons-material/Download';
import { useImageUrls } from '../hooks/useImageUrls';

// Centralized sizes for dashboard
const DASHBOARD_SIZES = {
  cardHeight: { xs: 260, sm: 280, md: 300 },
  thumbnail: { xs: 96, sm: 112, md: 128 },
} as const;

// Shared styles
const SHARED_STYLES = {
  contentContainer: {
    display: 'flex',
    gap: 2,
    mt: 1,
    width: '100%',
  },
  truncatedText: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.5em',
    maxHeight: '4.5em',
    wordBreak: 'break-word',
    whiteSpace: 'pre-line',
  },
  dashboardCard: {
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    minHeight: '140px',
    width: '100%',
    boxSizing: 'border-box',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    msUserSelect: 'none',
    height: DASHBOARD_SIZES.cardHeight,
    backgroundColor: '#F8F8FB',
    borderRadius: 2,
    border: 'none',
    boxShadow: undefined,
    '&:hover': {
      transform: 'translateY(-4px)',
      backgroundColor: '#F8F8FBBF',
      boxShadow: '0 2px 3px rgba(0,0,0,0.4)',
    },
    '&:active': {
      transform: 'translateY(-4px)',
      backgroundColor: '#F8F8FBBF',
      boxShadow: '0 2px 3px rgba(0,0,0,0.4)',
    },
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
  },
} as const;

// Utility functions
const formatDate = (timestamp: number) => {
  try {
    const timeInMs =
      timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;
    return format(new Date(timeInMs), 'MMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'No date';
  }
};

const getDocumentDate = (
  file: DiaryItem | DocumentItem
): number | string | undefined => {
  // For DocumentItem (from /api/documents), prioritize createdAt
  if ('source' in file && file.source) {
    return file.createdAt || file.timestamp || (file as any).created_at;
  }
  // For DiaryItem (from /api/diaries), use original logic
  return file.timestamp || file.createdAt;
};

const getValidDate = (item: DiaryItem | DocumentItem) => {
  const timestamp = getDocumentDate(item);
  if (!timestamp) return new Date();

  const timestampNum =
    typeof timestamp === 'number' ? timestamp : Number(timestamp);
  if (timestampNum && timestampNum.toString().length === 10) {
    return new Date(timestampNum * 1000);
  }
  return new Date(timestamp);
};

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { diaryItems } = useDiaryItems();
  const { documents } = useDocuments();
  const { tasks } = useTasks();
  const { instructions } = useInstructions();
  const { descriptions } = useDescriptions();
  const [activeOrganisation] = useActiveOrganisation();
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const [isLoadingFullSize, setIsLoadingFullSize] = React.useState(false);
  const [selectedDocument, setSelectedDocument] = React.useState<
    DiaryItem | DocumentItem | null
  >(null);

  // Filter documents to get files (items with content) - similar to FilesTable.tsx
  const files = React.useMemo(
    () => documents.filter((item) => item.content?.[0]),
    [documents]
  );

  // Prepare image sources for diaryItems
  const diaryImageSources = React.useMemo(
    () =>
      (diaryItems || [])
        .map((doc) => {
          const content = doc.content?.[0];
          return content && doc.id && content.hash
            ? {
                id: doc.id,
                hash: content.hash,
                path: content.path,
                thumbnail: content.thumbnail,
              }
            : null;
        })
        .filter(Boolean) as Array<{
        id: string;
        hash: string;
        path?: string;
        thumbnail?: string;
      }>,
    [diaryItems]
  );
  // Prepare image sources for files (using documents from useDocuments)
  const fileImageSources = React.useMemo(
    () =>
      (files || [])
        .map((file) => {
          const content = file.content?.[0];
          return content && file.id && content.hash
            ? {
                id: file.id,
                hash: content.hash,
                path: content.path,
                thumbnail: content.thumbnail,
                s3Url: (file as DocumentItem).s3Url || (content as any).s3Url,
              }
            : null;
        })
        .filter(Boolean) as Array<{
        id: string;
        hash: string;
        path?: string;
        thumbnail?: string;
        s3Url?: string;
      }>,
    [files]
  );
  // Merge all image sources
  const allImageSources = React.useMemo(
    () => [...diaryImageSources, ...fileImageSources],
    [diaryImageSources, fileImageSources]
  );
  // Use the shared hook for image loading
  const imageUrls = useImageUrls(allImageSources);

  const getMostRecentTask = () => {
    if (!tasks || tasks.length === 0) return null;
    return tasks.sort((a, b) => {
      const timeA =
        typeof a.timestamp === 'number' && !isNaN(a.timestamp)
          ? a.timestamp
          : 0;
      const timeB =
        typeof b.timestamp === 'number' && !isNaN(b.timestamp)
          ? b.timestamp
          : 0;
      return timeB - timeA;
    })[0];
  };

  const getStatusEmoji = (status: string | null) => {
    switch (status) {
      case 'IN_PROGRESS':
        return '🔄';
      case 'COMPLETED':
        return '✅';
      case 'PENDING':
        return '⏳';
      default:
        return '📝';
    }
  };

  const mostRecentDocument =
    diaryItems?.length > 0
      ? [...diaryItems].sort((a, b) => {
          const dateA = getValidDate(a).getTime();
          const dateB = getValidDate(b).getTime();
          return dateB - dateA;
        })[0]
      : null;

  const handleImageOpen = async (document: DiaryItem | DocumentItem) => {
    const photoContent = document.content?.[0];
    if (!photoContent?.path || !photoContent?.hash) return;

    setSelectedDocument(document);
    setSelectedImage(imageUrls[document.id]?.thumbnail || null);

    if (!imageUrls[document.id]?.fullSize) {
      setIsLoadingFullSize(true);
      if (typeof imageUrls.loadFullSize === 'function') {
        try {
          const url = await imageUrls.loadFullSize({
            id: document.id,
            hash: photoContent.hash,
            path: photoContent.path,
            thumbnail: photoContent.thumbnail,
            s3Url:
              (document as DocumentItem).s3Url || (photoContent as any).s3Url,
          });
          if (url) {
            setSelectedImage(url);
          }
        } catch (error) {
          console.error('Error loading image:', error);
        }
      }
      setIsLoadingFullSize(false);
    } else {
      setSelectedImage(imageUrls[document.id]?.fullSize);
    }
  };

  const handleImageClose = () => {
    setSelectedImage(null);
    setSelectedDocument(null);
  };

  const isPDF = React.useCallback((path?: string): boolean => {
    if (!path) return false;
    return path.toLowerCase().endsWith('.pdf');
  }, []);

  const handleDownload = async (document: DiaryItem | DocumentItem) => {
    const photoContent = document.content?.[0];
    if (!photoContent?.path || !photoContent?.hash) return;
    if (typeof imageUrls.loadFullSize === 'function') {
      try {
        const url = await imageUrls.loadFullSize({
          id: document.id,
          hash: photoContent.hash,
          path: photoContent.path,
          thumbnail: photoContent.thumbnail,
          s3Url:
            (document as DocumentItem).s3Url || (photoContent as any).s3Url,
        });
        if (url) {
          const link = window.document.createElement('a');
          link.href = url;
          link.download = `document-${format(getValidDate(document), 'yyyy-MM-dd-HH-mm')}.${photoContent.path.split('.').pop()}`;
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
        }
      } catch (error) {
        console.error('Error downloading document:', error);
      }
    }
  };

  const renderLatestContent = (content: string, timestamp: number) => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Typography variant="body2" sx={SHARED_STYLES.truncatedText}>
          {content}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {formatDate(timestamp)}
        </Typography>
      </Box>
    </>
  );

  const dashboardItems = [
    {
      title: 'Activity',
      content: (
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Typography variant="body2" color="text.secondary" align="left">
            {diaryItems?.length || 0}{' '}
            {t(diaryItems?.length === 1 ? '1-entry' : 'n-entries')}
          </Typography>
          {mostRecentDocument && (
            <Stack spacing={1}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary' }}
                align="left"
              >
                {t('Latest entry')}:
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                }}
              >
                <DocumentGridItem
                  document={mostRecentDocument}
                  onRowClick={() => navigate('/activity')}
                  onImageOpen={handleImageOpen}
                  onDownload={handleDownload}
                  imageUrls={imageUrls}
                  getValidDate={getValidDate}
                  showPendingBar={false}
                  transparentBackground={true}
                  thumbnailSize={DASHBOARD_SIZES.thumbnail}
                />
              </Box>
            </Stack>
          )}
        </Stack>
      ),
      icon: <DescriptionIcon sx={{ fontSize: 40 }} />,
      path: '/activity',
    },
    {
      title: 'Files',
      content: (
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Typography variant="body2" color="text.secondary" align="left">
            {files?.length || 0} {t(files?.length === 1 ? '1-file' : 'n-files')}
          </Typography>
          {files && files.length > 0 && (
            <Stack spacing={1}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary' }}
                align="left"
              >
                {t('Latest file')}:
              </Typography>
              {(() => {
                const sortedFiles = [...files].sort(
                  (a, b) =>
                    getValidDate(b).getTime() - getValidDate(a).getTime()
                );
                const latestFile = sortedFiles[0];
                return (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: DASHBOARD_SIZES.thumbnail,
                          height: DASHBOARD_SIZES.thumbnail,
                          borderRadius: 1,
                          overflow: 'hidden',
                          backgroundColor: '#FFFFFF !important',
                          flexShrink: 0,
                          '&:hover .fileActions': {
                            opacity: 1,
                          },
                        }}
                      >
                        {imageUrls[latestFile.id]?.thumbnail ? (
                          <img
                            src={imageUrls[latestFile.id].thumbnail || ''}
                            alt={latestFile.title || 'File thumbnail'}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <InsertDriveFileIcon
                              sx={{
                                fontSize: { xs: 32, sm: 36, md: 40 },
                                color: 'text.secondary',
                              }}
                            />
                          </Box>
                        )}
                        {/* Hover actions */}
                        <Box
                          className="fileActions"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            p: { xs: 0.25, sm: 0.5 },
                            display: 'flex',
                            gap: { xs: 0.25, sm: 0.5 },
                            opacity: 0,
                            transition: 'opacity 0.15s ease',
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageOpen(latestFile);
                            }}
                            sx={{
                              width: { xs: 28, sm: 32, md: 36 },
                              height: { xs: 28, sm: 32, md: 36 },
                              backgroundColor: 'rgba(255, 255, 255, 0.92)',
                              '&:hover': {
                                backgroundColor: 'white',
                                transform: 'scale(1.05)',
                              },
                              transition: 'all 0.15s ease',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                            }}
                          >
                            <ZoomInIcon
                              sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                            />
                          </IconButton>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(latestFile);
                            }}
                            sx={{
                              width: { xs: 28, sm: 32, md: 36 },
                              height: { xs: 28, sm: 32, md: 36 },
                              backgroundColor: 'rgba(255, 255, 255, 0.92)',
                              '&:hover': {
                                backgroundColor: 'white',
                                transform: 'scale(1.05)',
                              },
                              transition: 'all 0.15s ease',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                            }}
                          >
                            <DownloadIcon
                              sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          flex: 1,
                          minWidth: 0,
                          justifyContent: 'center',
                          height: DASHBOARD_SIZES.thumbnail,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {latestFile.category
                            ? t(latestFile.category)
                            : t('no category')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(getValidDate(latestFile).getTime())}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Stack>
          )}
        </Stack>
      ),
      icon: <InsertDriveFileIcon sx={{ fontSize: 40 }} />,
      path: '/files',
    },
    {
      title: 'Tasks',
      content: (
        <Stack spacing={1} sx={{ mt: 1, width: '100%' }}>
          <Typography variant="body2" color="text.secondary" align="left">
            {tasks?.length || 0} {t(tasks?.length === 1 ? '1-task' : 'n-tasks')}
          </Typography>
          {tasks && tasks.length > 0 && (
            <Box sx={SHARED_STYLES.contentContainer}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary' }}
                  align="left"
                >
                  {t('Latest task')}:
                </Typography>
                {(() => {
                  const latestTask = getMostRecentTask();
                  if (!latestTask) return null;
                  return renderLatestContent(
                    latestTask.content,
                    latestTask.timestamp
                  );
                })()}
              </Box>
            </Box>
          )}
        </Stack>
      ),
      icon: <TaskIcon sx={{ fontSize: 40 }} />,
      path: '/tasks',
    },
    {
      title: 'Description',
      content: (
        <Stack spacing={1} sx={{ mt: 1, width: '100%' }}>
          <Typography variant="body2" color="text.secondary" align="left">
            {descriptions?.length || 0}{' '}
            {t(descriptions?.length === 1 ? '1-description' : 'n-descriptions')}
          </Typography>
          {descriptions && descriptions.length > 0 && (
            <Box sx={SHARED_STYLES.contentContainer}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary' }}
                  align="left"
                >
                  {t('Latest description')}:
                </Typography>
                {renderLatestContent(
                  descriptions[descriptions.length - 1]?.content,
                  descriptions[descriptions.length - 1]?.timestamp
                )}
              </Box>
            </Box>
          )}
        </Stack>
      ),
      icon: <ArticleIcon sx={{ fontSize: 40 }} />,
      path: '/description',
    },
    {
      title: 'Instructions',
      content: (
        <Stack spacing={1} sx={{ mt: 1, width: '100%' }}>
          <Typography variant="body2" color="text.secondary" align="left">
            {instructions?.length || 0}{' '}
            {t(instructions?.length === 1 ? '1-instruction' : 'n-instructions')}
          </Typography>
          {instructions && instructions.length > 0 && (
            <Box sx={SHARED_STYLES.contentContainer}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary' }}
                  align="left"
                >
                  {t('Latest instruction')}:
                </Typography>
                {renderLatestContent(
                  instructions[instructions.length - 1]?.content,
                  instructions[instructions.length - 1]?.timestamp
                )}
              </Box>
            </Box>
          )}
        </Stack>
      ),
      icon: <InfoIcon sx={{ fontSize: 40 }} />,
      path: '/instructions',
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <GradientBackground sx={{ minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <HeaderBar
            currentPage={CurrentPages.Home}
            setCurrentPage={() => {}}
          />
          <Fade in={true} timeout={500}>
            <Box
              sx={{
                padding: '2rem',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: {
                    xs: '400px',
                    md: '824px',
                  },
                }}
              >
                <Typography variant="h4" component="h1">
                  {activeOrganisation?.organizationName || t('Dashboard')}
                </Typography>

                <Grid
                  container
                  spacing={3}
                  sx={{
                    mt: 0,
                    justifyContent: 'center',
                  }}
                >
                  {dashboardItems.map((item, index) => (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      key={index}
                      sx={{
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      <Paper
                        onClick={() => navigate(item.path)}
                        elevation={0}
                        sx={SHARED_STYLES.dashboardCard}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <Box sx={SHARED_STYLES.iconContainer}>
                            {React.cloneElement(item.icon, {
                              sx: { fontSize: 24 },
                            })}
                          </Box>
                          <Typography
                            variant="h6"
                            component="h2"
                            color="primary.main"
                          >
                            {t(item.title)}
                          </Typography>
                        </Box>
                        {item.content}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Fade>
        </Box>
      </GradientBackground>
      <FullSizePreview
        open={!!selectedImage}
        onClose={handleImageClose}
        imageUrl={selectedImage}
        isLoading={isLoadingFullSize}
        isPDF={
          selectedDocument?.content?.[0]?.path
            ? isPDF(selectedDocument.content?.[0]?.path)
            : false
        }
      />
    </ThemeProvider>
  );
};

export default DashboardPage;
